// src/pages/components/BuyerSuggestionPanel.jsx
//
// Broker-side panel that surfaces auto-scored buyer suggestions for
// a workspace. Fetches its own state, runs its own search kick-off,
// and lets the broker approve → creates DealBuyer row on the main
// pipeline, or dismiss → hides on the next search run.
//
// Drop-in usage:
//   <BuyerSuggestionPanel workspaceKey={workspaceKey} onApprove={reloadPipeline} />
//
// Backend contract:
//   POST /ai/workspaces/<key>/buyer-suggestions/search/
//        → { ok, run_id, n_top_surfaced, sources_hit, duration_ms }
//   GET  /ai/workspaces/<key>/buyer-suggestions/?status=pending
//        → { suggestions: [...], pagination }
//   PATCH /ai/workspaces/<key>/buyer-suggestions/<id>/
//        body: { action: "approve" | "dismiss", dismissed_reason? }

import React, { useCallback, useEffect, useState } from "react";
import { request } from "../../services/server";
import AcquirerProvenanceModal from "./AcquirerProvenanceModal";

const _DISMISS_REASONS = [
  "Fel storlek",
  "Redan kontaktad",
  "Inte aktiv köpare just nu",
  "Konkurrent — konfliktrisk",
  "Passar inte affären",
];

function BuyerSuggestionPanel({ workspaceKey, onApprove }) {
  const [suggestions, setSuggestions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searching, setSearching] = useState(false);
  const [error, setError] = useState("");
  const [lastRun, setLastRun] = useState(null);
  const [busyId, setBusyId] = useState(null);
  // Which acquirer the provenance modal is currently open for, if
  // any. ``null`` = closed. Stored as an object so the modal has
  // enough context (name) to render its header before the fetch
  // completes.
  const [provenanceOpen, setProvenanceOpen] = useState(null);

  const baseUrl = workspaceKey
    ? `/ai/workspaces/${encodeURIComponent(workspaceKey)}/buyer-suggestions/`
    : null;

  const load = useCallback(async () => {
    if (!baseUrl) return;
    setLoading(true);
    setError("");
    try {
      const data = await request(`${baseUrl}?status=pending`);
      setSuggestions(data?.suggestions || []);
    } catch (e) {
      setError(e?.message || "Kunde inte hämta köparförslag.");
    } finally {
      setLoading(false);
    }
  }, [baseUrl]);

  useEffect(() => {
    load();
  }, [load]);

  const runSearch = async () => {
    if (!baseUrl) return;
    setSearching(true);
    setError("");
    try {
      const result = await request(`${baseUrl}search/`, {
        method: "POST",
        body: { top_n: 30 },
      });
      if (!result?.ok) {
        setError(result?.error || "Sökningen misslyckades.");
      } else {
        setLastRun({
          scored: result.n_candidates_scored,
          surfaced: result.n_top_surfaced,
          newAcquirers: result.n_new_acquirers,
          sources: result.sources_hit || [],
          durationMs: result.duration_ms,
        });
        await load();
      }
    } catch (e) {
      setError(e?.message || "Sökningen misslyckades.");
    } finally {
      setSearching(false);
    }
  };

  const approve = async (row) => {
    if (busyId) return;
    setBusyId(row.id);
    try {
      await request(`${baseUrl}${row.id}/`, {
        method: "PATCH",
        body: { action: "approve" },
      });
      setSuggestions((prev) => prev.filter((r) => r.id !== row.id));
      onApprove && onApprove();
    } catch (e) {
      setError(e?.message || "Kunde inte godkänna.");
    } finally {
      setBusyId(null);
    }
  };

  const dismiss = async (row, reason) => {
    if (busyId) return;
    setBusyId(row.id);
    try {
      await request(`${baseUrl}${row.id}/`, {
        method: "PATCH",
        body: { action: "dismiss", dismissed_reason: reason || "" },
      });
      setSuggestions((prev) => prev.filter((r) => r.id !== row.id));
    } catch (e) {
      setError(e?.message || "Kunde inte avvisa.");
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div
      style={{
        border: "1px solid #e7e5e4",
        borderRadius: 8,
        padding: 16,
        background: "#fafaf7",
      }}
    >
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: 12,
        }}
      >
        <div>
          <h3 style={{ margin: 0, fontSize: 16 }}>Föreslagna köpare</h3>
          {lastRun && (
            <div style={{ fontSize: 12, color: "#78716c", marginTop: 4 }}>
              Senaste sökning: {lastRun.surfaced} kandidater
              {lastRun.newAcquirers > 0 &&
                ` (${lastRun.newAcquirers} nya)`} ·{" "}
              källor: {(lastRun.sources || []).join(", ") || "—"}
            </div>
          )}
        </div>
        <button
          type="button"
          onClick={runSearch}
          disabled={searching}
          style={{
            padding: "8px 16px",
            fontSize: 13,
            fontWeight: 600,
            background: "#3e6fa5",
            color: "white",
            border: "none",
            borderRadius: 6,
            cursor: searching ? "wait" : "pointer",
          }}
        >
          {searching ? "Söker…" : "Sök köpare"}
        </button>
      </div>

      {error && (
        <div
          style={{
            color: "#c0261a",
            fontSize: 13,
            padding: "8px 12px",
            background: "#fdecec",
            borderRadius: 4,
            marginBottom: 12,
          }}
        >
          {error}
        </div>
      )}

      {loading && <div style={{ color: "#78716c", fontSize: 13 }}>Hämtar…</div>}

      {!loading && suggestions.length === 0 && (
        <div style={{ color: "#78716c", fontSize: 13, padding: "16px 0" }}>
          Inga förslag ännu. Klicka <strong>Sök köpare</strong> för att köra
          en sökning över SCB + BolagsAPI + FöretagsAPI.
        </div>
      )}

      {suggestions.length > 0 && (
        <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
          {suggestions.map((row) => (
            <SuggestionRow
              key={row.id}
              row={row}
              busy={busyId === row.id}
              onApprove={() => approve(row)}
              onDismiss={(reason) => dismiss(row, reason)}
              onOpenProvenance={() =>
                setProvenanceOpen({
                  id: row.acquirer.id,
                  name: row.acquirer.name,
                })
              }
            />
          ))}
        </div>
      )}

      {provenanceOpen && (
        <AcquirerProvenanceModal
          acquirerId={provenanceOpen.id}
          acquirerName={provenanceOpen.name}
          onClose={() => setProvenanceOpen(null)}
        />
      )}
    </div>
  );
}

function SuggestionRow({ row, busy, onApprove, onDismiss, onOpenProvenance }) {
  const [showDismissMenu, setShowDismissMenu] = useState(false);
  const scoreColor =
    row.overall_score >= 75
      ? "#2f6f34"
      : row.overall_score >= 50
      ? "#b45309"
      : "#78716c";

  return (
    <div
      style={{
        border: "1px solid #e7e5e4",
        borderRadius: 6,
        padding: 12,
        background: "white",
        display: "flex",
        gap: 12,
        alignItems: "flex-start",
      }}
    >
      <div
        style={{
          minWidth: 48,
          height: 48,
          borderRadius: 6,
          background: scoreColor,
          color: "white",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          fontWeight: 700,
          fontSize: 18,
        }}
        title="Matchningspoäng 0-100"
      >
        {row.overall_score}
      </div>

      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ display: "flex", gap: 8, alignItems: "baseline" }}>
          <button
            type="button"
            onClick={onOpenProvenance}
            title="Klicka för datakällor och uppdateringshistorik"
            style={{
              background: "none",
              border: "none",
              padding: 0,
              cursor: "pointer",
              fontSize: 14,
              fontWeight: 700,
              color: "#1c1917",
              textAlign: "left",
              textDecoration: "underline",
              textDecorationColor: "#a8a29e",
              textDecorationStyle: "dotted",
              textUnderlineOffset: 3,
            }}
          >
            {row.acquirer.name || "(namnlös köpare)"}
          </button>
          {row.acquirer.org_number && (
            <span style={{ fontSize: 11, color: "#78716c" }}>
              {row.acquirer.org_number}
            </span>
          )}
          {row.acquirer.ownership_type && (
            <span
              style={{
                fontSize: 10,
                padding: "1px 6px",
                background: "#e7e5e4",
                borderRadius: 3,
                color: "#57534e",
              }}
            >
              {row.acquirer.ownership_type}
            </span>
          )}
        </div>

        {(row.positive_factors || []).length > 0 && (
          <ul
            style={{
              listStyle: "none",
              padding: 0,
              margin: "6px 0 0 0",
              fontSize: 12,
              color: "#44403c",
            }}
          >
            {(row.positive_factors || []).slice(0, 3).map((r, i) => (
              <li key={i} style={{ marginBottom: 2 }}>
                ✓ {r}
              </li>
            ))}
          </ul>
        )}
        {(row.negative_factors || []).length > 0 && (
          <ul
            style={{
              listStyle: "none",
              padding: 0,
              margin: "4px 0 0 0",
              fontSize: 12,
              color: "#78716c",
            }}
          >
            {(row.negative_factors || []).slice(0, 2).map((r, i) => (
              <li key={i}>× {r}</li>
            ))}
          </ul>
        )}
      </div>

      <div
        style={{
          display: "flex",
          gap: 6,
          flexDirection: "column",
          alignItems: "flex-end",
        }}
      >
        <button
          type="button"
          onClick={onApprove}
          disabled={busy}
          style={{
            padding: "6px 12px",
            fontSize: 12,
            fontWeight: 600,
            background: "#2f6f34",
            color: "white",
            border: "none",
            borderRadius: 4,
            cursor: busy ? "wait" : "pointer",
          }}
        >
          Godkänn
        </button>
        <div style={{ position: "relative" }}>
          <button
            type="button"
            onClick={() => setShowDismissMenu((v) => !v)}
            disabled={busy}
            style={{
              padding: "6px 12px",
              fontSize: 12,
              color: "#c0261a",
              background: "white",
              border: "1px solid #c0261a",
              borderRadius: 4,
              cursor: busy ? "wait" : "pointer",
            }}
          >
            Avvisa ▾
          </button>
          {showDismissMenu && (
            <div
              style={{
                position: "absolute",
                right: 0,
                top: "100%",
                marginTop: 4,
                background: "white",
                border: "1px solid #e7e5e4",
                borderRadius: 4,
                boxShadow: "0 4px 12px rgba(0,0,0,0.08)",
                zIndex: 10,
                minWidth: 200,
              }}
            >
              {_DISMISS_REASONS.map((reason) => (
                <button
                  key={reason}
                  type="button"
                  onClick={() => {
                    setShowDismissMenu(false);
                    onDismiss(reason);
                  }}
                  style={{
                    display: "block",
                    width: "100%",
                    textAlign: "left",
                    padding: "8px 12px",
                    fontSize: 12,
                    background: "none",
                    border: "none",
                    borderBottom: "1px solid #f5f5f4",
                    cursor: "pointer",
                  }}
                >
                  {reason}
                </button>
              ))}
              <button
                type="button"
                onClick={() => {
                  setShowDismissMenu(false);
                  onDismiss("");
                }}
                style={{
                  display: "block",
                  width: "100%",
                  textAlign: "left",
                  padding: "8px 12px",
                  fontSize: 12,
                  background: "none",
                  border: "none",
                  color: "#78716c",
                  cursor: "pointer",
                }}
              >
                Utan anledning
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default BuyerSuggestionPanel;
